import React, { useEffect, useState } from "react"
import { Sparklines, SparklinesLine } from "react-sparklines"
import { FaStar } from "react-icons/fa"
import "./style/TrendingTokensSection.scss"
import { useTranslation } from "react-i18next"

type Token = {
  id: string
  symbol: string
  name: string
  image: string
  price: number
  change: number
  sparkline: number[]
  marketCap: number
  volume: number
}

const tabs = [
  { key: "hot", label: "🔥 Hot" },
  { key: "gainers", label: "🚀 Gainers" },
  { key: "newest", label: "🆕 Newest" }
]

const TrendingTokensSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>("hot")
  const [tokens, setTokens] = useState<Token[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    const fetchTokens = async () => {
      setLoading(true)
      setError("")
      try {
        // 1) Fetch top 10 by trending / gainers / newest
        let url =
          activeTab === "hot"
            ? `https://api.coingecko.com/api/v3/search/trending`
            : `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=${
                activeTab === "gainers" ? "price_change_percentage_24h_desc" : "gecko_desc"
              }&per_page=3&page=1&sparkline=true`

        const res = await fetch(url)
        const data = await res.json()

        let list: any[]
        if (activeTab === "hot") {
          // map trending to markets
          const ids = data.coins.slice(0, 3).map((c: any) => c.item.id)
          const markets = await fetch(
            `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${ids.join(
              ","
            )}&sparkline=true`
          ).then((r) => r.json())
          list = markets
        } else {
          list = data
        }

        // 2) shape to our Token type
        const shaped: Token[] = list.map((c: any) => ({
          id: c.id,
          symbol: c.symbol.toUpperCase(),
          name: c.name,
          image: c.image,
          price: c.current_price,
          change: c.price_change_percentage_24h,
          sparkline: c.sparkline_in_7d.price,
          marketCap: c.market_cap,
          volume: c.total_volume
        }))
        setTokens(shaped)
      } catch (e) {
        console.error(e)
        setError("Failed to fetch token data")
      } finally {
        setLoading(false)
      }
    }

    fetchTokens()
  }, [activeTab])
 const { t } = useTranslation()
  return (
    <section className="trending-section">
      <h2>{t("home.trendingTokens")}</h2>
      <div className="tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={activeTab === t.key ? "active" : ""}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="status">Loading…</div>}
      {error && <div className="status error">{error}</div>}

      {!loading && !error && (
        <div className="token-list">
          {tokens.map((tk) => (
            <div key={tk.id} className="token-card">
              <div className="watchlist">
                <FaStar />
              </div>
              <div className="token-header">
                <img src={tk.image} alt={tk.symbol} />
                <div>
                  <strong>{tk.symbol}</strong>
                  <span>{tk.name}</span>
                </div>
              </div>

              <div className="token-chart">
                <Sparklines data={tk.sparkline} width={100} height={30}>
                 <SparklinesLine
  color={tk.change >= 0 ? "#22c55e" : "#ef4444"}
  style={{
    strokeWidth: 0.6,
    fill: tk.change >= 0 ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)"
  }}
/>

                </Sparklines>
              </div>

              <div className="token-stats">
                <div className="price">${tk.price.toFixed(2)}</div>
                <div className={`change ${tk.change >= 0 ? "up" : "down"}`}>
                  {tk.change >= 0 ? "▲" : "▼"} {Math.abs(tk.change).toFixed(2)}%
                </div>
              </div>

              <div className="token-meta">
                <div title={`Market Cap: $${tk.marketCap.toLocaleString()}`}>
                  Cap: ${tk.marketCap.toLocaleString()}
                </div>
                <div title={`24h Volume: $${tk.volume.toLocaleString()}`}>
                  Vol: ${tk.volume.toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

export default TrendingTokensSection
